import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Home, CalendarDays } from 'lucide-react';
import { bookingPaymentService, formatCurrency } from '../../services/bookingPaymentService';

interface PaymentResult {
  success: boolean;
  bookingId: number;
  bookingNumber?: string;
  amount: number;
  message: string;
}

/**
 * Booking Payment Success Page
 *
 * This page handles the return from PayPal after payment approval.
 * It captures the payment and displays the result.
 */
const BookingPaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const capturePayment = async () => {
      const bookingId = searchParams.get('bookingId');
      const provider = searchParams.get('provider') || 'paypal';
      const token = searchParams.get('token'); // PayPal order ID from redirect
      // v1.8.33 (Review Befund 5): Stripe hängt die Checkout-Session an; nur diese Zahlung gilt als "gerade bezahlt"
      const stripeSessionId = searchParams.get('session_id');
      const isDone = (p: { status: string }) => p.status === 'Completed' || p.status === 'Paid' || p.status === 'succeeded';
      const pickPayment = (list?: { status: string; amount: number; reference?: string | null }[]) =>
        stripeSessionId
          ? list?.find(p => p.reference === stripeSessionId && isDone(p))
          : list?.find(p => isDone(p));

      console.log('BookingPaymentSuccess - URL params:', { bookingId, provider, token });

      if (!bookingId) {
        setError('Keine Buchungs-ID gefunden');
        setIsLoading(false);
        return;
      }

      try {
        // For PayPal: Capture with token
        if (provider === 'paypal') {
          if (!token) {
            setError('Keine PayPal-Transaktions-ID gefunden');
            setIsLoading(false);
            return;
          }

          console.log('Capturing PayPal payment for booking:', bookingId);

          const result = await bookingPaymentService.capturePayPalPayment(
            parseInt(bookingId),
            token
          );

          console.log('Capture result:', result);

          setPaymentResult({
            success: result.success,
            bookingId: result.bookingId,
            bookingNumber: result.bookingNumber,
            amount: result.amount,
            message: result.message,
          });
        }
        // For Stripe/Klarna: Check payment status (Webhook has already processed)
        else {
          console.log(`Checking ${provider} payment status for booking:`, bookingId);

          const statusResult = await bookingPaymentService.getPaymentStatus(
            parseInt(bookingId)
          );

          console.log('Payment status result:', statusResult);

          // Check if payment was successful
          const hasSuccessfulPayment = !!pickPayment(statusResult.payments);

          if (hasSuccessfulPayment) {
            const latestPayment = pickPayment(statusResult.payments);
            setPaymentResult({
              success: true,
              bookingId: parseInt(bookingId),
              amount: latestPayment?.amount || 0,
              message: 'Zahlung erfolgreich!',
            });
          } else {
            // Wait a moment and check again (webhook might be delayed)
            await new Promise(resolve => setTimeout(resolve, 2000));

            const retryResult = await bookingPaymentService.getPaymentStatus(
              parseInt(bookingId)
            );

            const retrySuccess = !!pickPayment(retryResult.payments);

            if (retrySuccess) {
              const latestPayment = pickPayment(retryResult.payments);
              setPaymentResult({
                success: true,
                bookingId: parseInt(bookingId),
                amount: latestPayment?.amount || 0,
                message: 'Zahlung erfolgreich!',
              });
            } else {
              // Show success anyway - webhook will process soon
              setPaymentResult({
                success: true,
                bookingId: parseInt(bookingId),
                amount: 0,
                message: 'Zahlung wird verarbeitet. Sie erhalten eine Bestätigung per E-Mail.',
              });
            }
          }
        }
      } catch (err: any) {
        console.error('Payment capture/check error:', err);
        setError(err.message || 'Fehler bei der Zahlungsverarbeitung');
      } finally {
        setIsLoading(false);
      }
    };

    capturePayment();
  }, [searchParams]);

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Zahlung wird verarbeitet...
          </h2>
          <p className="text-gray-600">
            Bitte warten Sie, während wir Ihre Zahlung bestätigen.
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Zahlung fehlgeschlagen
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Link
                to="/booking"
                className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <CalendarDays className="w-5 h-5 inline-block mr-2" />
                Zurück zur Buchung
              </Link>
              <Link
                to="/dashboard"
                className="block w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                <Home className="w-5 h-5 inline-block mr-2" />
                Mein Konto
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (paymentResult?.success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Zahlung erfolgreich!
            </h2>
            <p className="text-gray-600 mb-6">
              Vielen Dank! Ihre Zahlung wurde erfolgreich verarbeitet.
            </p>

            {/* Payment Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-3">Zahlungsdetails</h3>
              <div className="space-y-2 text-sm">
                {paymentResult.bookingNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Buchungsnummer:</span>
                    <span className="font-medium text-gray-900">
                      {paymentResult.bookingNumber}
                    </span>
                  </div>
                )}
                {/* T-121 (v1.8.33): Betrag nur zeigen, wenn die Zahlung schon gemeldet ist (vorher "0,00 €"); sonst "in Bearbeitung" */}
                {paymentResult.amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bezahlter Betrag:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(paymentResult.amount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium text-green-600">{paymentResult.amount > 0 ? 'Bezahlt' : 'In Bearbeitung, Bestätigung folgt per E-Mail'}</span>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>Nächste Schritte:</strong> Sie erhalten in Kürze eine Bestätigungs-E-Mail
                mit allen Details zu Ihrer Buchung.
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="space-y-3">
              <Link
                to={`/booking-confirmation/${paymentResult.bookingId}`}
                className="block w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Buchungsbestätigung ansehen
              </Link>
              <Link
                to="/dashboard"
                className="block w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                <Home className="w-5 h-5 inline-block mr-2" />
                Mein Konto
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment not successful but no error
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Zahlung nicht abgeschlossen
          </h2>
          <p className="text-gray-600 mb-6">
            {paymentResult?.message || 'Die Zahlung konnte nicht abgeschlossen werden.'}
          </p>
          <div className="space-y-3">
            <Link
              to="/booking"
              className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Erneut versuchen
            </Link>
            <Link
              to="/dashboard"
              className="block w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              <Home className="w-5 h-5 inline-block mr-2" />
              Mein Konto
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPaymentSuccess;
